import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Printer, Send } from "lucide-react";
import { CONTACT, LEMONADE_ADDONS } from "@/data/products";
import { useProducts } from "@/context/ProductsContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYMENT_OPTIONS = ["Cash", "Zelle", "Venmo", "Square", "Apple Pay"];

const PRINT_SECTIONS = [
  { title: "Regular Lemonade — $5", items: [["Classic", null]] },
  {
    title: "Original Flavors — $6",
    items: [["Strawberry"], ["Mango"], ["Peach"], ["Pineapple"], ["Blue Raspberry"], ["Watermelon"], ["Sour Candy"], ["Lavender"], ["Passion Fruit"]],
  },
  {
    title: "Fall Specials — $8",
    items: [
      ["Caramel Apple", "Crisp apple + buttery caramel with a sweet-tart fall finish."],
      ["Maple Peach", "Juicy peach with warm maple sweetness and refreshing lemonade zing."],
      ["Pumpkin Spice Lemonade", "Bright lemonade with creamy pumpkin, vanilla and warm fall spice."],
      ["Hibiscus Soursop Blood Orange", "Tangy hibiscus, tropical soursop and bold blood orange with a juicy citrus finish."],
    ],
  },
];

const fieldCls =
  "w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm text-[#1a1a1a] placeholder:text-zinc-400 focus:border-pink-400 focus:outline-none";

const QtyRow = ({ label, price, sub, qty, onQty, testid }) => (
  <div
    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-colors cursor-pointer ${
      qty > 0 ? "border-pink-500 bg-pink-50" : "border-zinc-200 bg-white"
    }`}
    onClick={() => (qty > 0 ? onQty(0) : onQty(1))}
    data-testid={testid}
  >
    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${qty > 0 ? "bg-pink-500 border-pink-500 text-white" : "border-zinc-300"}`}>
      {qty > 0 && "✓"}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-[#1a1a1a]">
        {label} <span className="text-zinc-400 font-semibold">· ${price.toFixed(2)}</span>
      </p>
      {sub && <p className="text-xs text-zinc-500 leading-snug">{sub}</p>}
    </div>
    {qty > 0 && (
      <input
        type="number"
        min="1"
        value={qty}
        data-testid={`${testid}-qty`}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onQty(Math.max(0, parseInt(e.target.value) || 0))}
        className="w-16 rounded-lg border-2 border-pink-300 bg-white px-2 py-1.5 text-sm font-bold text-center text-[#1a1a1a] focus:outline-none"
        aria-label={`${label} quantity`}
      />
    )}
  </div>
);

export const BulkOrderForm = () => {
  const { products } = useProducts();
  const [info, setInfo] = useState({
    name: "", phone: "",
    orderDate: new Date().toISOString().slice(0, 10),
    needDate: "", method: "Pickup",
  });
  const [qtys, setQtys] = useState({});
  const [addonQtys, setAddonQtys] = useState({});
  const [payMethods, setPayMethods] = useState([]);
  const [notes, setNotes] = useState("");
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const setQty = (id) => (v) => setQtys((prev) => ({ ...prev, [id]: typeof v === "number" ? v : parseInt(v.target.value) || 0 }));
  const setAddonQty = (id) => (v) => setAddonQtys((prev) => ({ ...prev, [id]: typeof v === "number" ? v : parseInt(v.target.value) || 0 }));
  const togglePay = (m) => setPayMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const lani = useMemo(() => products.filter((p) => p.brand === "lemonade" && p.price != null), [products]);
  const sections = useMemo(() => [
    { title: "Regular Lemonade — $5", items: lani.filter((p) => p.cat === "Classic") },
    { title: "Original Flavors — $6", items: lani.filter((p) => p.cat === "Flavored") },
    { title: "Fall Specials — $8", items: lani.filter((p) => p.cat === "Fall Flavors") },
  ], [lani]);

  const selectedDrinks = lani.filter((p) => (qtys[p.id] || 0) > 0);
  const selectedAddons = LEMONADE_ADDONS.filter((a) => (addonQtys[a.id] || 0) > 0);
  const drinkCount = selectedDrinks.reduce((s, p) => s + qtys[p.id], 0);
  const lemTotal = selectedDrinks.reduce((s, p) => s + p.price * qtys[p.id], 0);
  const addonTotal = selectedAddons.reduce((s, a) => s + a.price * addonQtys[a.id], 0);
  const deliveryFee = info.method === "Delivery" ? (drinkCount >= 6 || lemTotal >= 25 ? 0 : 5) : 0;
  const totalDue = lemTotal + addonTotal + deliveryFee;

  const submit = async (e) => {
    e.preventDefault();
    if (!info.name.trim() || !info.phone.trim()) return toast.error("Name and phone are needed");
    if (drinkCount === 0) return toast.error("Pick at least one lemonade");

    const payload = {
      name: info.name.trim(),
      phone: info.phone.trim(),
      order_date: info.orderDate,
      need_date: info.needDate,
      method: info.method,
      drinks: selectedDrinks.map((p) => ({ id: p.id, name: p.name.replace(" — 34oz", ""), qty: qtys[p.id], price: p.price })),
      addons: selectedAddons.map((a) => ({ id: a.id, name: a.name, qty: addonQtys[a.id], price: a.price })),
      lemonades_total: +lemTotal.toFixed(2),
      addons_total: +addonTotal.toFixed(2),
      delivery_fee: deliveryFee,
      total_due: +totalDue.toFixed(2),
      payment_methods: payMethods,
      notes: notes.trim(),
    };

    try {
      await axios.post(`${API}/bulk-requests`, payload);
      setSent(true);
      toast.success("Order request sent to Lani HQ!");
    } catch {
      toast.error("Couldn't save the request — the text still works, just hit send");
    }

    const lines = [
      "🍋 LANI LEMONADE ORDER FORM (34oz cups)",
      `Name: ${payload.name}`,
      `Phone: ${payload.phone}`,
      `Order Date: ${payload.order_date}`,
      payload.need_date && `Pickup/Delivery Date: ${payload.need_date}`,
      `Pickup / Delivery: ${payload.method}`,
      "",
      "DRINKS:",
      ...payload.drinks.map((d) => `• ${d.qty}× ${d.name} — $${(d.qty * d.price).toFixed(2)}`),
      payload.addons.length ? "" : null,
      payload.addons.length ? "ADD-ONS:" : null,
      ...payload.addons.map((a) => `• ${a.qty}× ${a.name} — $${(a.qty * a.price).toFixed(2)}`),
      "",
      `Lemonades: $${payload.lemonades_total.toFixed(2)} · Add-ons: $${payload.addons_total.toFixed(2)} · Delivery: $${payload.delivery_fee.toFixed(2)}`,
      `TOTAL DUE: $${payload.total_due.toFixed(2)}`,
      payMethods.length ? `Payment: ${payMethods.join(", ")}` : null,
      notes.trim() ? `Notes: ${notes.trim()}` : null,
    ].filter((l) => l !== null);

    window.location.href = `sms:${CONTACT.phone.replace(/\D/g, "")}?&body=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <section data-testid="bulk-order-section" className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
      <div className="rounded-2xl border-2 border-[#1a1a1a] bg-pink-400 shadow-[8px_8px_0_#1a1a1a] p-6 sm:p-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-100">🎉 Events · Offices · Parties</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Lani Lemonade Order Form</h2>
          <p className="mt-2 text-sm font-bold text-pink-50">
            34oz Cups — Regular Lemonade $5 · Original Flavors $6 · Fall Specials $8
          </p>
          <p className="mt-1 text-xs text-pink-100">6+ drinks · one drop-off · FREE delivery — fill it out and it lands in our dashboard + a pre-written text.</p>
          <button type="button" onClick={() => window.print()} data-testid="bulk-print-form"
            className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-[#1a1a1a] bg-white text-[#1a1a1a] text-xs font-bold px-5 py-2.5 shadow-[3px_3px_0_#1a1a1a] active:translate-y-0.5 active:shadow-none transition-all">
            <Printer className="w-3.5 h-3.5" /> Print Blank Form (for events)
          </button>
        </div>

        {sent && (
          <div className="mt-6 rounded-xl border-2 border-[#1a1a1a] bg-emerald-300 px-5 py-4 text-center" data-testid="bulk-sent-banner">
            <p className="font-extrabold text-[#1a1a1a]">✓ Request received!</p>
            <p className="text-sm text-zinc-700">We'll text you to confirm. Your messages app also opened with the details — hit send there too if it didn't.</p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="bulk-order-form">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white mb-3">Customer Information</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="bulk-name" value={info.name} onChange={set("name")} placeholder="Name *" className={fieldCls} />
              <input data-testid="bulk-phone" value={info.phone} onChange={set("phone")} placeholder="Phone *" className={fieldCls} />
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-pink-100 block mb-1">Order Date</label>
                <input data-testid="bulk-order-date" type="date" value={info.orderDate} onChange={set("orderDate")} className={fieldCls} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-pink-100 block mb-1">Pickup/Delivery Date</label>
                <input data-testid="bulk-need-date" type="date" value={info.needDate} onChange={set("needDate")} className={fieldCls} />
              </div>
              <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                {["Pickup", "Delivery"].map((m) => (
                  <button type="button" key={m} data-testid={`bulk-method-${m.toLowerCase()}`} onClick={() => setInfo({ ...info, method: m })}
                    className={`rounded-xl border-2 py-3 text-sm font-bold transition-colors ${info.method === m ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-zinc-200 bg-white text-zinc-500"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {sections.map((sec) => (
            <div key={sec.title}>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-white mb-3">{sec.title}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {sec.items.map((p) => (
                  <QtyRow
                    key={p.id}
                    testid={`bulk-drink-${p.id}`}
                    label={p.name.replace(" — 34oz", "")}
                    price={p.price}
                    sub={p.cat === "Fall Flavors" ? p.description : null}
                    qty={qtys[p.id] || 0}
                    onQty={setQty(p.id)}
                  />
                ))}
              </div>
            </div>
          ))}

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white mb-3">Add-Ons</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {LEMONADE_ADDONS.map((a) => (
                <QtyRow
                  key={a.id}
                  testid={`bulk-addon-${a.id}`}
                  label={`${a.emoji} ${a.name}`}
                  price={a.price}
                  qty={addonQtys[a.id] || 0}
                  onQty={setAddonQty(a.id)}
                />
              ))}
            </div>
          </div>

          <div className="rounded-xl border-2 border-[#1a1a1a] bg-white p-5 space-y-1.5 text-sm text-[#1a1a1a]" data-testid="bulk-totals">
            <div className="flex justify-between"><span>Lemonades ({drinkCount})</span><span data-testid="bulk-lem-total">${lemTotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Add-ons</span><span>${addonTotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span data-testid="bulk-delivery-fee">{info.method === "Delivery" ? (deliveryFee === 0 ? "FREE" : `$${deliveryFee.toFixed(2)}`) : "—"}</span></div>
            <div className="flex justify-between font-extrabold text-lg pt-2 border-t-2 border-[#1a1a1a]"><span>TOTAL DUE</span><span data-testid="bulk-total-due">${totalDue.toFixed(2)}</span></div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white mb-3">Payment</p>
            <div className="flex flex-wrap gap-2" data-testid="bulk-payment-options">
              {PAYMENT_OPTIONS.map((m) => (
                <button type="button" key={m} data-testid={`bulk-pay-${m.toLowerCase().replace(" ", "-")}`} onClick={() => togglePay(m)}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-bold transition-colors ${payMethods.includes(m) ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-zinc-200 bg-white text-zinc-500"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white mb-3">Special Instructions / Notes</p>
            <textarea data-testid="bulk-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Ice preferences, timing, anything else…" className={`${fieldCls} resize-none`} />
          </div>

          <button type="submit" data-testid="bulk-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1a1a] text-white font-bold py-4 text-base hover:bg-black active:scale-[0.98] transition-transform">
            <Send className="w-4 h-4" /> Send Order Request
          </button>
          <p className="text-center text-xs text-pink-100">Thank you for sipping with Lani Lemonade! 💕🍋</p>
        </form>
      </div>

      {createPortal(
        <div id="print-blank-form">
        <div className="print-form-body">
          <h1>LANI LEMONADE ORDER FORM</h1>
          <p><strong>34 OZ CUPS</strong> — Regular Lemonade $5 · Original Flavors $6 · Fall Specials $8</p>
          <h2>CUSTOMER INFORMATION</h2>
          <div className="pf-grid">
            <p>Name: <span className="pf-line"></span></p>
            <p>Phone: <span className="pf-line"></span></p>
            <p>Order Date: <span className="pf-line-sm"></span></p>
            <p>Pickup/Delivery Date: <span className="pf-line-sm"></span></p>
            <p>Pickup / Delivery: <span className="pf-line"></span></p>
          </div>
          {PRINT_SECTIONS.map((sec) => (
            <div key={sec.title}>
              <h2>{sec.title.toUpperCase()}</h2>
              <div className="pf-grid">
                {sec.items.map(([label, desc]) => (
                  <div key={label} style={{ marginTop: "4pt" }}>
                    <div className="pf-row">
                      <span className="pf-box"></span>
                      <span>{label}</span>
                      <span style={{ marginLeft: "auto" }}>Qty: <span className="pf-line-sm"></span></span>
                    </div>
                    {desc && <p style={{ fontSize: "7.5pt", color: "#444", margin: "1pt 0 0 15pt" }}>{desc}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <h2>ADD-ONS</h2>
          <div className="pf-grid">
            {LEMONADE_ADDONS.map((a) => (
              <div className="pf-row" key={a.id}>
                <span className="pf-box"></span>
                <span>{a.name} — ${a.price.toFixed(2)}</span>
                <span style={{ marginLeft: "auto" }}>Qty: <span className="pf-line-sm"></span></span>
              </div>
            ))}
          </div>
          <h2>ORDER TOTAL</h2>
          <p>Lemonades: $ <span className="pf-line-sm"></span> · Add-ons: $ <span className="pf-line-sm"></span> · Delivery: $ <span className="pf-line-sm"></span></p>
          <p><strong>TOTAL DUE: $ <span className="pf-line"></span></strong></p>
          <h2>PAYMENT</h2>
          <div className="pf-row" style={{ gap: "14pt" }}>
            {PAYMENT_OPTIONS.map((m) => (
              <span key={m}><span className="pf-box"></span> {m}</span>
            ))}
          </div>
          <h2>SPECIAL INSTRUCTIONS / NOTES</h2>
          <p className="pf-line" style={{ display: "block", width: "100%" }}></p>
          <p className="pf-line" style={{ display: "block", width: "100%" }}></p>
          <p className="pf-line" style={{ display: "block", width: "100%" }}></p>
          <p style={{ marginTop: "14pt", fontWeight: 700 }}>Thank you for sipping with Lani Lemonade! 💕🍋 · Text {CONTACT.phone}</p>
        </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default BulkOrderForm;
